import { ColorEnum } from '@/constants/color'
import { useRouterCanGoBackParamSubscriber } from '@/features/navigation/hooks/can-go-back'
import Button from '@/features/openTabs/components/AccountActionButton'
import AccountHeader from '@/features/openTabs/components/AccountHeader'
import AccountItemCard from '@/features/openTabs/components/AccountItemCard'
import AccountList from '@/features/openTabs/components/AccountList'
import AccountPaymentFooter from '@/features/openTabs/components/AccountPaymentFooter'
import CancelPaymentModal from '@/features/openTabs/components/CancelPaymentModal'
import CashCalculatorModal from '@/features/openTabs/components/CashCalculatorModal'
import CreateAccountModal from '@/features/openTabs/components/CreateAccountModal'
import EmptyState from '@/features/openTabs/components/EmptyState'
import SplitHeader from '@/features/openTabs/components/SplitHeader'
import { useOpenTabs } from '@/features/openTabs/contexts/OpenTabsData'
import { useOpenTabHeader } from '@/features/openTabs/hooks/useOpenTabHeader'
import useAccountPayment from '@/features/openTabs/pages/PaymentPage/hooks/useAccountPayment'
import { formatAccountListPrice, toAccountListItem } from '@/features/openTabs/utils/viewModels'
import Typography from '@/features/ui/typography'
import { useScreenLayout } from '@/hooks/screen'
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollView, View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

const PaymentPage: FC = () => {
    useRouterCanGoBackParamSubscriber()

    const { t } = useTranslation('app')
    const layout = useScreenLayout()
    const { styles } = useStyles(stylesheet)
    const router = useRouter()
    const { mode } = useLocalSearchParams<{ mode?: string }>()
    const {
        openTabs,
        closedAccounts,
        activeStatus,
        selectedAccountId,
        openTabsTotal,
        closedAccountsTotal,
        currency,
        setActiveStatus,
        selectAccount,
        createAccount,
        creatingAccount,
    } = useOpenTabs()
    const {
        selectedAccount,
        summaryItems,
        paymentTotalPrice,
        paymentPriceItems,
        paymentMethods,
        paymentMethod,
        submitting,
        canPay,
        canCalculate,
        paymentErrorMessage,
        calculator,
        handlePay,
        handleCancel,
        cancelOpen,
        handleCancelConfirm,
        handleCancelKeep,
        handleCashConfirm,
        handleCashCancel,
        handleOpenCalculator,
        handlePaymentMethodChange,
        handleDigit,
        handleQuickAmount,
        handleBackspace,
    } = useAccountPayment()
    const [createOpen, setCreateOpen] = useState(false)
    const [accountName, setAccountName] = useState('')
    useOpenTabHeader({
        title: t(mode === 'split' ? 'openTabs.actions.split' : 'drawerItem.openTabs'),
        onBack: () => router.back(),
    })

    if (!selectedAccount || selectedAccount.status === 'closed') {
        return <Redirect href="/open-tabs" />
    }

    const returnToAccounts = () => router.dismissTo('/open-tabs')
    const handleCreateConfirm = async () => {
        if (creatingAccount) return

        if (await createAccount(accountName)) returnToAccounts()
    }

    return (
        <View style={styles.container}>
            {layout !== 'compact' && (
                <View style={styles.accountList}>
                    <AccountList
                        accounts={(activeStatus === 'open' ? openTabs : closedAccounts).map(
                            toAccountListItem
                        )}
                        status={activeStatus}
                        counts={{ open: openTabs.length, closed: closedAccounts.length }}
                        selectedAccountId={selectedAccountId ?? undefined}
                        total={formatAccountListPrice(
                            activeStatus === 'open' ? openTabsTotal : closedAccountsTotal,
                            currency
                        )}
                        onStatusChange={(status) =>
                            handleCancel(() => {
                                setActiveStatus(status)
                                returnToAccounts()
                            })
                        }
                        onAccountSelect={(accountId) => {
                            if (accountId === selectedAccountId) return
                            handleCancel(() => {
                                selectAccount(accountId)
                                returnToAccounts()
                            })
                        }}
                        onCreateAccount={() => handleCancel(() => setCreateOpen(true))}
                    />
                </View>
            )}
            <View style={styles.payment}>
                {layout === 'compact' && mode === 'split' ? (
                    <View style={styles.splitHeader}>
                        <SplitHeader
                            accountName={selectedAccount.name}
                            accountNumber={selectedAccount.number}
                        />
                    </View>
                ) : (
                    <AccountHeader
                        accountName={selectedAccount.name}
                        accountNumber={selectedAccount.number}
                        onClose={() => handleCancel()}
                    />
                )}
                <ScrollView
                    contentContainerStyle={styles.content}
                    style={styles.scroll}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.items} testID="open-account-payment-items">
                        <Typography.Heading variant="h5">
                            {t(
                                mode === 'split' && layout === 'compact'
                                    ? 'openTabs.split.toPay'
                                    : 'order.detail.items'
                            )}
                        </Typography.Heading>
                        {summaryItems.map((item) => (
                            <AccountItemCard key={item.id} item={item} />
                        ))}
                        {!summaryItems.length && (
                            <>
                                <EmptyState
                                    title={t(
                                        mode === 'split'
                                            ? 'openTabs.empty.splitTitle'
                                            : 'openTabs.payment.emptyTitle'
                                    )}
                                    description={t(
                                        mode === 'split'
                                            ? 'openTabs.empty.splitDescription'
                                            : 'openTabs.payment.emptyDescription'
                                    )}
                                />
                                <Button
                                    color="secondary"
                                    onPress={() =>
                                        router.dismissTo(
                                            mode === 'split' ? '/open-tabs/split' : '/open-tabs'
                                        )
                                    }
                                    testID="open-account-payment-review"
                                >
                                    {t('openTabs.payment.reviewSelection')}
                                </Button>
                            </>
                        )}
                    </View>
                </ScrollView>
                <AccountPaymentFooter
                    totalPrice={paymentTotalPrice}
                    priceItems={paymentPriceItems}
                    canPay={canPay}
                    canCalculate={canCalculate}
                    onCalculator={handleOpenCalculator}
                    paymentMethods={paymentMethods}
                    paymentMethod={paymentMethod}
                    errorMessage={paymentErrorMessage}
                    submitting={submitting}
                    onPaymentMethodChange={handlePaymentMethodChange}
                    onCancel={() => handleCancel()}
                    onPay={handlePay}
                />
                {cancelOpen && (
                    <CancelPaymentModal onConfirm={handleCancelConfirm} onKeep={handleCancelKeep} />
                )}
            </View>
            {createOpen && (
                <CreateAccountModal
                    name={accountName}
                    onChange={setAccountName}
                    loading={creatingAccount}
                    onConfirm={handleCreateConfirm}
                    onCancel={() => {
                        if (!creatingAccount) returnToAccounts()
                    }}
                />
            )}
            {calculator.open && (
                <CashCalculatorModal
                    currency={selectedAccount.currency}
                    helper={calculator.helper}
                    totalPrice={calculator.totalPrice}
                    receivedPrice={calculator.receivedPrice}
                    remainingPrice={calculator.remainingPrice}
                    isCovered={calculator.isCovered}
                    canConfirm={calculator.canConfirm}
                    errorMessage={calculator.errorMessage}
                    submitting={submitting}
                    onQuickAmount={handleQuickAmount}
                    onDigit={handleDigit}
                    onBackspace={handleBackspace}
                    onCancel={handleCashCancel}
                    onConfirm={handleCashConfirm}
                />
            )}
        </View>
    )
}

const stylesheet = createStyleSheet({
    container: { flex: 1, flexDirection: 'row', backgroundColor: ColorEnum.WHITE },
    accountList: {
        flex: 1,
        minWidth: 0,
        borderRightWidth: 1,
        borderRightColor: ColorEnum.GRAY_200,
    },
    payment: { flex: 1, minWidth: 0, minHeight: 0 },
    scroll: { flex: 1, minHeight: 0 },
    content: { flexGrow: 1 },
    splitHeader: { paddingTop: 16 },
    items: { padding: 16, gap: 8 },
})

export default PaymentPage
